/**
 * UpsellPushSettings.jsx
 * Reusable panel for Upsell-Push configuration (used in Mobile App admin + Shop Settings).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Percent } from 'lucide-react';
import UpsellTemplatesManager from '@/components/products/UpsellTemplatesManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpsellPushSettings = ({ showTemplates = true }) => {
  const [upsellPush, setUpsellPush] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/upsell-push`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUpsellPush(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateUpsell = (field, value) =>
    setUpsellPush((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/shop/upsell-push`,
        {
          enabled: !!upsellPush.enabled,
          min_customer_points: Number(upsellPush.min_customer_points) || 0,
          max_customer_points: Number(upsellPush.max_customer_points) || 50,
          inactive_days: Number(upsellPush.inactive_days) || 7,
          schedule_day: Number(upsellPush.schedule_day),
          schedule_hour: Number(upsellPush.schedule_hour),
          title_template: upsellPush.title_template || '',
          body_template: upsellPush.body_template || '',
          only_if_upsell_available: !!upsellPush.only_if_upsell_available,
          use_templates: upsellPush.use_templates !== false,
          email_fallback_enabled: upsellPush.email_fallback_enabled !== false,
          whatsapp_fallback_enabled: upsellPush.whatsapp_fallback_enabled !== false,
          whatsapp_delay_hours: Number(upsellPush.whatsapp_delay_hours) || 24,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Upsell-Push Einstellungen gespeichert');
    } catch (e) {
      console.error(e);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !upsellPush) {
    return <div className="text-gray-500 text-sm">Lade Upsell-Einstellungen…</div>;
  }

  return (
    <div className="space-y-4" data-testid="upsell-push-section">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold flex items-center gap-2 text-gray-900">
            <Percent className="w-4 h-4 text-amber-600" /> Upsell-Push Benachrichtigungen
          </h4>
          <p className="text-sm text-gray-500">
            Kunden erinnern: „Mit X Punkten mehr bekommst du Y% Rabatt"
          </p>
        </div>
        <label className="flex items-center gap-2 text-gray-900">
          <input
            type="checkbox"
            checked={!!upsellPush.enabled}
            onChange={(e) => updateUpsell('enabled', e.target.checked)}
            className="rounded"
            data-testid="upsell-push-enabled"
          />
          <span className="text-sm">Aktiv</span>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Min. Punkte-Rest</label>
          <Input
            type="number"
            min="0"
            value={upsellPush.min_customer_points ?? 0}
            onChange={(e) => updateUpsell('min_customer_points', e.target.value)}
            className="text-sm"
            data-testid="upsell-min-points"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Max. Punkte-Rest</label>
          <Input
            type="number"
            min="0"
            value={upsellPush.max_customer_points ?? 50}
            onChange={(e) => updateUpsell('max_customer_points', e.target.value)}
            className="text-sm"
            data-testid="upsell-max-points"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Inaktiv seit (Tage)</label>
          <Input
            type="number"
            min="0"
            value={upsellPush.inactive_days ?? 7}
            onChange={(e) => updateUpsell('inactive_days', e.target.value)}
            className="text-sm"
            data-testid="upsell-inactive-days"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Stunde (0-23)</label>
          <Input
            type="number"
            min="0"
            max="23"
            value={upsellPush.schedule_hour ?? 10}
            onChange={(e) => updateUpsell('schedule_hour', e.target.value)}
            className="text-sm"
            data-testid="upsell-hour"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 block mb-1">Versand-Tag</label>
        <select
          value={upsellPush.schedule_day ?? -1}
          onChange={(e) => updateUpsell('schedule_day', e.target.value)}
          className="w-full border border-gray-200 bg-gray-50 text-gray-900 rounded-md px-3 py-2 text-sm"
          data-testid="upsell-day"
        >
          <option value={-1}>Täglich</option>
          <option value={0}>Montag</option>
          <option value={1}>Dienstag</option>
          <option value={2}>Mittwoch</option>
          <option value={3}>Donnerstag</option>
          <option value={4}>Freitag</option>
          <option value={5}>Samstag</option>
          <option value={6}>Sonntag</option>
        </select>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 block mb-1">Titel-Vorlage</label>
        <Input
          value={upsellPush.title_template || ''}
          onChange={(e) => updateUpsell('title_template', e.target.value)}
          className="text-sm"
          data-testid="upsell-title"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500 block mb-1">Nachricht-Vorlage</label>
        <textarea
          value={upsellPush.body_template || ''}
          onChange={(e) => updateUpsell('body_template', e.target.value)}
          rows={3}
          className="w-full border border-gray-200 bg-gray-50 text-gray-900 rounded-md px-3 py-2 text-sm"
          data-testid="upsell-body"
        />
      </div>

      <div className="text-xs text-gray-500 bg-gray-50 rounded p-3 space-y-1 border border-gray-200">
        <p className="font-medium text-gray-600">Verfügbare Platzhalter:</p>
        <p>
          <code>{'{first_name}'}</code>, <code>{'{current_points}'}</code>,{' '}
          <code>{'{next_points}'}</code>, <code>{'{next_label}'}</code>
        </p>
        <p>
          <code>{'{extra_points}'}</code>, <code>{'{extra_cost}'}</code>,{' '}
          <code>{'{next_discount_percent}'}</code>
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-900">
        <input
          type="checkbox"
          checked={!!upsellPush.only_if_upsell_available}
          onChange={(e) => updateUpsell('only_if_upsell_available', e.target.checked)}
          className="rounded"
        />
        Nur senden, wenn ein Upsell verfügbar ist
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
        <label className="flex items-center gap-2 text-sm text-gray-900">
          <input
            type="checkbox"
            checked={upsellPush.use_templates !== false}
            onChange={(e) => updateUpsell('use_templates', e.target.checked)}
            className="rounded"
          />
          A/B-Templates nutzen (wenn vorhanden)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-900">
          <input
            type="checkbox"
            checked={upsellPush.email_fallback_enabled !== false}
            onChange={(e) => updateUpsell('email_fallback_enabled', e.target.checked)}
            className="rounded"
            data-testid="upsell-email-fallback"
          />
          E-Mail Fallback (wenn keine App)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-900">
          <input
            type="checkbox"
            checked={upsellPush.whatsapp_fallback_enabled !== false}
            onChange={(e) => updateUpsell('whatsapp_fallback_enabled', e.target.checked)}
            className="rounded"
            data-testid="upsell-whatsapp-fallback"
          />
          WhatsApp Fallback (zeitversetzt)
        </label>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">
            WhatsApp-Verzögerung (Std.)
          </label>
          <Input
            type="number"
            min="1"
            max="168"
            value={upsellPush.whatsapp_delay_hours ?? 24}
            onChange={(e) => updateUpsell('whatsapp_delay_hours', e.target.value)}
            className="text-sm w-32"
            data-testid="upsell-whatsapp-delay"
          />
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-upsell-push-btn">
        {saving ? 'Speichern...' : 'Upsell-Push Einstellungen speichern'}
      </Button>

      {showTemplates && (
        <div className="pt-6 border-t border-gray-200 mt-4">
          <UpsellTemplatesManager />
        </div>
      )}
    </div>
  );
};

export default UpsellPushSettings;
